import { Alert, AlertDescription } from '@chakra-ui/alert'
import { Link, VStack } from '@chakra-ui/layout'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text as ChakraText,
  Textarea,
} from '@chakra-ui/react'
import { Select } from '@chakra-ui/select'
import useFetch, { CachePolicies } from 'use-http'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import type { ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import useToast from '../../hooks/useToast'
import type { UserSessionResponse } from '../../interfaces'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  onClose(): void
}

interface FormProps {
  reason:
    | ''
    | 'DIFFERENT_IK_ACCOUNT'
    | 'SWITCH_TO_DIFF_SERVICE'
    | 'FEATURE_REQUEST'
    | 'PRICING_CONCERN'
    | 'IMPROVEMENT_IN_SERVICE'
    | 'ISSUE_IN_INTEGRATION'
    | 'OTHER'
  feedback?: string
  newServiceName?: string
}

const CancelSubscriptionModal = ({
  isOpen,
  onClose,
  user,
}: Props): ReactElement => {
  const { t } = useTranslation()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const toast = useToast()

  const Options = [
    { label: t('select_a_reason', 'Select a reason'), val: '' },
    {
      val: 'DIFFERENT_IK_ACCOUNT',
      label: t('different_ik_account', 'I have different Imagekit account'),
    },
    {
      val: 'SWITCH_TO_DIFF_SERVICE',
      label: t('switch_to_diff_service', 'Switching to different service'),
    },
    {
      val: 'FEATURE_REQUEST',
      label: t('feature_request', 'Feature not available'),
    },
    {
      val: 'PRICING_CONCERN',
      label: t('pricing_concern', 'Pricing not suitable'),
    },
    {
      val: 'IMPROVEMENT_IN_SERVICE',
      label: t('improvement_in_service', 'Improvements in service'),
    },
    {
      val: 'ISSUE_IN_INTEGRATION',
      label: t('issue_in_integration', 'Integration issues'),
    },
    { val: 'OTHER', label: t('other', 'Other') },
  ]

  const Descriptions = {
    FEATURE_REQUEST: t('feature_request_description'),
    PRICING_CONCERN: t('pricing_concern_description'),
    IMPROVEMENT_IN_SERVICE: t('improvement_in_service_description'),
    ISSUE_IN_INTEGRATION: (
      <Trans t={t} i18nKey="issue_in_integration_description">
        In case you faced issues while integrating your storage or servers with
        ImageKit, you can refer to our
        <Link
          href="https://docs.imagekit.io/integration/integration-overview"
          isExternal
        >
          integration guides
        </Link>
        for step-by-step instructions for the same. In case, you are not able to
        find a relevant guide or need one-on-one guidance for migrating a
        complex setup to ImageKit, share your feedback in the textbox below &
        our customer support team will get back to you.
      </Trans>
    ),
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ reason: '', newServiceName: '', feedback: '' }}
          onSubmit={async (values, actions) => {
            if (user) {
              const resp = await request.post(
                `/api/clients/${user.clientNumber}/settings/cancel-subscription`,
                {
                  reason: values.reason,
                  newServiceName: values.newServiceName ?? '',
                  feedback: values.feedback ?? '',
                },
              )

              if (request.error) {
                if (resp?.reason) {
                  actions.setErrors({
                    reason: resp.reason,
                  })
                  return
                }
              }

              if (request.error) {
                toast({
                  description: t(
                    'something_went_wrong',
                    'Something went wrong!',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                return
              }

              toast({
                title: t(
                  'successfully_cancelled_subscription',
                  'Successfully cancelled subscription. Reloading dashboard.',
                ),
                description: t(
                  'reloading_your_dashboard',
                  'Reloading your dashboard.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })

              setTimeout(() => {
                window.location.href = '/dashboard'
              }, 2000)
            }
          }}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <ModalHeader>
                {t(
                  'cancel_imagekit_subscription',
                  'Cancel ImageKit Subscription',
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <Alert status="warning">
                    <AlertDescription color={"imagekitGray.900"}>
                      <ChakraText>
                        If your usage is within the limits of the <Link isExternal={true} href="https://imagekit.io/plans/">current public free plan</Link>, you won’t be charged. Otherwise, an invoice will be generated immediately based on your usage so far.
                      </ChakraText>
                      <br />
                      <ChakraText>
                        By proceeding, you confirm the cancellation of your subscription. Your account will immediately switch to the free tier.
                      </ChakraText>
                    </AlertDescription>
                  </Alert>
                  <ChakraText>
                    Help us improve our service - please share you reason for
                    cancellation:
                  </ChakraText>
                  <Field name="reason">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.reason && form.touched.reason,
                        )}
                        isRequired
                      >
                        <FormLabel>Reason</FormLabel>
                        <Select {...field}>
                          {Options.map(x => (
                            <option key={x.val} value={x.val}>
                              {x.label}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>
                          {form.errors.reason}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  {values.reason === 'SWITCH_TO_DIFF_SERVICE' ? (
                    <Field name="newServiceName">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          isInvalid={Boolean(
                            form.errors.newServiceName &&
                              form.touched.newServiceName,
                          )}
                        >
                          <FormLabel>New Service Name</FormLabel>
                          <Input
                            {...field}
                            placeholder={t('which_service', 'Which Service ?')}
                          />
                          <FormErrorMessage>
                            {form.errors.newServiceName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  ) : null}
                  <ChakraText>{Descriptions[values.reason] ?? null}</ChakraText>
                  {values.reason !== '' ? (
                    <Field name="feedback">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          isInvalid={Boolean(
                            form.errors.feedback && form.touched.feedback,
                          )}
                        >
                          <FormLabel>
                            {t('additional_feedback', 'Additional feedback')}
                          </FormLabel>
                          <Textarea
                            {...field}
                            placeholder={t(
                              'additional_feedback_description',
                              'Additional feedback you wish to provide for this concern',
                            )}
                          />
                          <FormErrorMessage>
                            {form.errors.feedback}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  ) : null}
                </VStack>
              </ModalBody>

              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel')}
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {t('cancel_subscription', 'Cancel Subscription')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CancelSubscriptionModal
